import { useState, useEffect } from "react";
import { useApi } from "../../../hooks/useApi";

/** Local YYYY-MM-DD — same clock useDashboardData uses for team-hours. */
const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

function hoursFrom(h) {
  const trackedMinutes = Number(h.totalMinutes) || 0;
  const idleMinutes = Number(h.totalIdleTimeMinutes) || 0;
  const activeMinutes = h.totalActiveTimeMinutes != null
    ? Number(h.totalActiveTimeMinutes)
    : Math.max(0, trackedMinutes - idleMinutes);
  return { trackedMinutes, idleMinutes, activeMinutes };
}

/**
 * Realtime KPI — same source Dashboard V2 used (`team-hours` / `today-hours`).
 * Hours + active members: closed sessions + still-clocked-in (screenshots)
 * + stealth gap-fill. Not the classic-overview raw duration sum.
 */
export function useLiveTeamHours(range, member) {
  const api = useApi();
  const [livePeople, setLivePeople] = useState(null);
  const [loading, setLoading] = useState(true);

  const startKey = range?.start ? fmt(range.start) : null;
  const endKey = range?.end ? fmt(range.end) : null;

  useEffect(() => {
    if (!startKey || !endKey) return undefined;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ startDate: startKey, endDate: endKey });
    const url = member
      ? `/api/v1/live-analytics/today-hours?userId=${encodeURIComponent(member)}&${qs}`
      : `/api/v1/live-analytics/team-hours?${qs}`;

    const start = new Date(`${startKey}T12:00:00`);
    const end = new Date(`${endKey}T12:00:00`);
    const span = Math.max(1, Math.round((end - start) / 86400000) + 1);
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (span - 1));
    const prevQs = new URLSearchParams({ startDate: fmt(prevStart), endDate: fmt(prevEnd) });
    const prevUrl = member
      ? `/api/v1/live-analytics/today-hours?userId=${encodeURIComponent(member)}&${prevQs}`
      : `/api/v1/live-analytics/team-hours?${prevQs}`;

    Promise.all([
      api.get(url),
      api.get(prevUrl).catch(() => null),
    ])
      .then(([res, prevRes]) => {
        if (!alive) return;
        const h = res.data?.data || {};
        const hrs = hoursFrom(h);
        const prevTrackedMinutes = Number(prevRes?.data?.data?.totalMinutes) || 0;
        if (member) {
          setLivePeople({
            activeMembers: hrs.trackedMinutes > 0 ? 1 : 0,
            teamSize: 1,
            ...hrs,
            prevTrackedMinutes,
          });
        } else {
          setLivePeople({
            activeMembers: h.activeMembers ?? 0,
            teamSize: h.teamSize ?? 0,
            ...hrs,
            prevTrackedMinutes,
          });
        }
      })
      .catch(() => { if (alive) setLivePeople(null); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [api, startKey, endKey, member]);

  return { livePeople, liveLoading: loading };
}

export default useLiveTeamHours;
